import { Component, computed, inject } from '@angular/core';
import { RegistryService } from '../../services/RegistryService';
import { LayoutService } from '../../services/LayoutService';
import { PanelMenu } from 'primeng/panelmenu';
import { RouterLink } from '@angular/router';
import { translate, TranslocoService } from '@jsverse/transloco';
import { AuthService } from '../../services/AuthService';
import { FormsModule } from '@angular/forms';
import { NgOptimizedImage } from '@angular/common';
import { LocationSwitcher } from './location-switcher';
import { MenuItem } from 'primeng/api';
import dayjs from 'dayjs/esm';

const ServiceIconMap: { [key: string]: string } = {
  "lite_schedule": "pi pi-calendar"
}

@Component({
  selector: 'app-menu',
  imports: [
    PanelMenu,
    RouterLink,
    FormsModule,
    NgOptimizedImage,
    LocationSwitcher
  ],
  template: `
    <div class="h-full flex flex-col gap-1">
      <div class="justify-center mb-4 sm:flex hidden">
        <a routerLink="/dashboard" class="flex">
          <img ngSrc="/logo.svg" [height]="80" [width]="80"
               style="max-height: 80px;max-width:80px;margin:auto 0;" alt="logo" class="dark:hidden">
          <img ngSrc="/logo.dark.svg" [height]="80" [width]="80"
               style="max-height: 80px;max-width:80px;margin:auto 0;" alt="logo" class="hidden dark:block">
        </a>
      </div>
      <app-location-switcher class="block sm:hidden"/>
      <p-panel-menu [model]="items()" [multiple]="true"/>

      @if (trialInfo(); as info) {
        <div routerLink="/billing/" (click)="layoutService.mobileMenuOpen.set(false)" class="cursor-pointer mt-auto p-3.5 rounded-xl bg-surface-50 dark:bg-surface-900 border border-surface-200 dark:border-surface-800 flex flex-col gap-2.5 transition-all duration-300 hover:border-surface-300 dark:hover:border-surface-700">
          <div class="flex justify-between items-center text-xs font-semibold text-surface-700 dark:text-surface-300">
            <span class="flex items-center gap-1.5">
              <i class="pi pi-clock text-surface-400 dark:text-surface-500"></i>
              {{ t.translate('Trial Period') }}
            </span>
            <span [class]="info.class" class="text-[11px] font-bold px-1.5 py-0.5 rounded-sm bg-surface-100 dark:bg-surface-800">
              {{ info.label }}
            </span>
          </div>
          
          <div class="w-full bg-surface-200 dark:bg-surface-800 rounded-full h-1.5 overflow-hidden">
            <div class="h-full rounded-full transition-all duration-500 ease-out" 
                 [style.width.%]="info.percent"
                 [class]="info.barClass"></div>
          </div>
          
          <div class="text-[10px] text-surface-500 dark:text-surface-400 flex justify-between items-center">
            <span>{{ info.progressText }}</span>
            <span>14 {{ t.translate('days total') }}</span>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
    }
  `]
})
export class MenuComponent {
  public auth = inject(AuthService)
  public t = inject(TranslocoService)
  public registry = inject(RegistryService);
  public layoutService = inject(LayoutService);

  public trialInfo = computed(() => {
    const user = this.auth.user();
    const company = user?.company;
    if (!company || company.status !== 'trial' || !company.date_created) {
      return null;
    }

    const TRIAL_DURATION_DAYS = 14;
    const created = dayjs(company.date_created);
    const trialEnd = created.add(TRIAL_DURATION_DAYS, 'day');
    const today = dayjs().startOf('day');
    const endOfTrial = trialEnd.startOf('day');
    const diffDays = endOfTrial.diff(today, 'day');

    const elapsedDays = Math.max(0, Math.min(TRIAL_DURATION_DAYS, TRIAL_DURATION_DAYS - diffDays));
    const percent = (elapsedDays / TRIAL_DURATION_DAYS) * 100;

    let label = '';
    let textClass = '';
    let barClass = '';
    let progressText = '';

    if (diffDays > 0) {
      label = `${diffDays} ${diffDays === 1 ? translate('day left') : translate('days left')}`;
      progressText = `${elapsedDays} ${translate('of')} ${TRIAL_DURATION_DAYS} ${translate('days used')}`;
      if (diffDays > 7) {
        textClass = 'text-emerald-600 dark:text-emerald-400 font-bold';
        barClass = 'bg-emerald-500 dark:bg-emerald-400';
      } else if (diffDays > 3) {
        textClass = 'text-amber-500 dark:text-amber-400 font-bold';
        barClass = 'bg-amber-500 dark:bg-amber-400';
      } else {
        textClass = 'text-rose-500 dark:text-rose-400 font-bold';
        barClass = 'bg-rose-500 dark:bg-rose-400';
      }
    } else if (diffDays === 0) {
      label = `0 ${translate('days left')}`;
      textClass = 'text-rose-500 dark:text-rose-400 font-bold';
      barClass = 'bg-rose-500 dark:bg-rose-400';
      progressText = translate('Last day of trial');
    } else {
      const overdueDays = Math.abs(diffDays);
      label = `${overdueDays} ${overdueDays === 1 ? translate('day overdue') : translate('days overdue')}`;
      textClass = 'text-rose-600 dark:text-rose-500 font-bold';
      barClass = 'bg-rose-600 dark:bg-rose-500';
      progressText = translate('Trial expired');
    }

    return {
      label,
      class: textClass,
      barClass,
      percent,
      progressText
    };
  });

  public items = computed(() => {
    const menu: MenuItem[] = [
      { label: translate('Dashboard'), icon: 'pi pi-home', routerLink: '/dashboard', command: () => this.layoutService.mobileMenuOpen.set(false) },
      { label: translate('Employees'), icon: 'pi pi-user', routerLink: '/hr/employees', command: () => this.layoutService.mobileMenuOpen.set(false) },
      { label: translate('Positions'), icon: 'pi pi-briefcase', routerLink: '/hr/positions', command: () => this.layoutService.mobileMenuOpen.set(false) }
    ]
    this.registry.services().forEach(s => {
      if (s.name === 'manageusers') {
        return;
      }
      const cl = this.auth.current_locale() ?? "he_il"
      const lang = this.registry.languages().find(l => l.locale === cl)
      const names = s.names || [];
      const _n = names.find(n => n.language_id === lang?.id)
      const n = _n ? _n.name : s.name;
      const icon = ServiceIconMap[s.name] ?? 'pi pi-question-circle';
      menu.push({
        label: n,
        icon,
        routerLink: '/service/' + s.name + '/',
        command: () => this.layoutService.mobileMenuOpen.set(false)
      });
    });
    menu.push({
      label: translate('Billing'),
      icon: 'pi pi-credit-card',
      routerLink: '/billing/',
      command: () => this.layoutService.mobileMenuOpen.set(false)
    });
    return menu;
  })
}
